import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Row, Col, Button, Card, Space, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { MailOutlined, SendOutlined, CustomerServiceOutlined, RocketOutlined } from '@ant-design/icons';
import SEO from '../../components/SEO';
import './ContactPage.css';

const { Title, Paragraph } = Typography;

const ContactPage: React.FC = () => {
  const { t } = useTranslation('landing');

  return (
    <>
      <SEO
        title="Контакты QATE | Связаться с командой платформы управления тестированием"
        description="Свяжитесь с командой QATE для получения поддержки, консультаций или партнерских предложений. Telegram, email, форма обратной связи. Мы всегда готовы помочь!"
        keywords="контакты QATE, поддержка QA платформы, связаться с QATE, техподдержка, партнерство, contact us, support team, QA помощь"
        canonicalUrl="/contact"
        ogTitle="Контакты QATE | Связаться с командой платформы"
        ogDescription="Свяжитесь с командой QATE для получения поддержки, консультаций или партнерских предложений. Мы всегда готовы помочь!"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": {
            "@type": "Organization",
            "name": "QATE",
            "url": "https://qate.ru",
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+7-XXX-XXX-XX-XX",
                "contactType": "customer support",
                "areaServed": "RU",
                "availableLanguage": ["Russian", "English"]
              },
              {
                "@type": "ContactPoint",
                "email": "contact@qate.ru",
                "contactType": "customer support",
                "areaServed": "RU"
              }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "RU",
              "addressLocality": "Россия"
            }
          }
        }}
      />
      <div className="contact-page">
        <div className="contact-container">
          {/* Hero Section */}
          <Row justify="center" align="middle" className="hero-section">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <div className="alpha-tag">
                <Tag color="blue">
                  {t('landing.hero.alpha')}
                </Tag>
              </div>
              <Title level={1} className="hero-title">{t('landing.contact.title')}</Title>
              <Paragraph className="hero-subtitle">
                {t('landing.contact.subtitle')}
              </Paragraph>
              <Paragraph className="hero-description">
                {t('landing.contact.description')}
              </Paragraph>
            </Col>
          </Row>

          {/* Contact Information */}
          <Row gutter={[32, 32]} className="contact-info-section">
            <Col xs={24} md={8}>
              <Card hoverable className="contact-card">
                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                  <MailOutlined style={{ fontSize: '48px', color: '#1a56db' }} />
                  <Title level={4}>{t('landing.contact.info.email.title')}</Title>
                  <Paragraph style={{ textAlign: 'center' }}>
                    <a href={`mailto:${t('landing.contact.info.email.content')}`}>
                      {t('landing.contact.info.email.content')}
                    </a>
                  </Paragraph>
                </Space>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card hoverable className="contact-card">
                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                  <SendOutlined style={{ fontSize: '48px', color: '#1a56db' }} />
                  <Title level={4}>{t('landing.contact.info.telegram.title')}</Title>
                  <Paragraph style={{ textAlign: 'center' }}>
                    <a href="https://t.me/QAappbot" target="_blank" rel="noopener noreferrer">
                      {t('landing.contact.info.telegram.content')}
                    </a>
                  </Paragraph>
                  <Paragraph style={{ fontSize: '14px', textAlign: 'center', color: '#666' }}>
                    {t('landing.contact.info.telegram.description')}
                  </Paragraph>
                </Space>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card hoverable className="contact-card">
                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                  <CustomerServiceOutlined style={{ fontSize: '48px', color: '#1a56db' }} />
                  <Title level={4}>{t('landing.contact.info.support.title')}</Title>
                  <Paragraph style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                    {t('landing.contact.info.support.description')}
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* CTA Section */}
          <Row justify="center" className="contact-cta">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <Title level={2}>{t('landing.contact.cta.title')}</Title>
              <Paragraph className="cta-subtitle">
                {t('landing.contact.cta.subtitle')}
              </Paragraph>
              <Space size="middle" className="cta-buttons">
                <Link to="/register">
                  <Button 
                    type="primary" 
                    size="large"
                    className="cta-button primary-button"
                    icon={<RocketOutlined />}
                  >
                    {t('landing.contact.cta.register')}
                  </Button>
                </Link>
                <a href="https://t.me/QAappbot" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="large"
                    className="cta-button secondary-button"
                    icon={<SendOutlined />}
                  >
                    {t('landing.contact.cta.telegram')}
                  </Button>
                </a>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default ContactPage; 